import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Grid,
  Pagination,
  Tabs,
  Tab
} from '@mui/material';
import { 
  Save as SaveIcon, 
  AccountCircle as AvatarIcon, 
  Casino as GachaIcon,
  Wallpaper as WallpaperIcon
} from '@mui/icons-material';
import DashboardLayout from '../../components/Dashboard/DashboardLayout';
import SidebarWithAccordion from '../../components/Dashboard/SidebarWithAccordion';
import { createTeacherSidebarConfig } from '../../components/Dashboard/TeacherSidebarConfig';
import { AuthContext } from '../../contexts/AuthContext';
import axios from '../../api/axios';
import AvatarCard from '../../components/Avatar/AvatarCard';
import AvatarFrameCard from '../../components/Avatar/AvatarFrameCard';
import GachaPoolDialog from '../../components/Avatar/GachaPoolDialog';
import { BACKGROUND_OPTIONS, getSidebarBackground, setSidebarBackground, COLOR_SCHEME_OPTIONS, getColorScheme, setColorScheme, getCustomPalette, setCustomPalette } from '../../constants/backgroundSettings';

function TeacherSettingsPage() {
  const { user, selectedCourse, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Settings state
  const [name, setName] = useState(user?.name || '');
  
  // Avatar state
  const [avatars, setAvatars] = useState([]);
  const [currentAvatar, setCurrentAvatar] = useState(null);
  const [selectedAvatarId, setSelectedAvatarId] = useState(null); // Temporary selection
  const [loadingAvatars, setLoadingAvatars] = useState(true);
  const [gachaPoolDialogOpen, setGachaPoolDialogOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [ownedAvatarsPage, setOwnedAvatarsPage] = useState(1);
  const [mainTab, setMainTab] = useState(0); // 0: 个人设置, 1: 头像管理, 2: 抽卡, 3: 背景设置
  const [selectedBackground, setSelectedBackground] = useState(() => getSidebarBackground());
  const [selectedColorScheme, setSelectedColorScheme] = useState(() => getColorScheme());
  const [customPalette, setCustomPaletteState] = useState(() => getCustomPalette());
  
  // Avatar frame state
  const [frames, setFrames] = useState([]);
  const [currentFrame, setCurrentFrame] = useState(null);
  const [selectedFrameId, setSelectedFrameId] = useState(null); // Temporary selection
  const [loadingFrames, setLoadingFrames] = useState(true);
  
  // Pagination for owned avatars: 2 rows, 6 items per row = 12 items per page
  const ownedAvatarsPerPage = 12;
  
  useEffect(() => {
    if (user) {
      setName(user.name || '');
    }
  }, [user]);

  useEffect(() => {
    setSelectedBackground(getSidebarBackground());
    setSelectedColorScheme(getColorScheme());
    setCustomPaletteState(getCustomPalette());
  }, [mainTab]);

  useEffect(() => {
    fetchAvatars();
    fetchFrames();
  }, []);

  const fetchAvatars = async () => {
    try {
      setLoadingAvatars(true);
      const [avatarsResponse, currentResponse] = await Promise.all([
        axios.get('/avatars'),
        axios.get('/avatars/current')
      ]);
      setAvatars(avatarsResponse.data);
      const current = currentResponse.data;
      setCurrentAvatar(current);
      setSelectedAvatarId(current?.id || null); // Initialize selected avatar
    } catch (err) {
      console.error('Error fetching avatars:', err);
    } finally {
      setLoadingAvatars(false);
    }
  };

  const handleSelectAvatar = (avatarId) => {
    // Only update temporary selection, don't save yet
    setSelectedAvatarId(avatarId);
  };

  const fetchFrames = async () => {
    try {
      setLoadingFrames(true);
      const [framesResponse, currentResponse] = await Promise.all([
        axios.get('/avatar-frames'),
        axios.get('/avatar-frames/current')
      ]);
      setFrames(framesResponse.data || []);
      const current = currentResponse.data;
      setCurrentFrame(current);
      setSelectedFrameId(current?.id || null); // Initialize selected frame
    } catch (err) {
      console.error('Error fetching frames:', err);
      setFrames([]);
    } finally {
      setLoadingFrames(false);
    }
  };

  const handleSelectFrame = (frameId) => {
    // Only update temporary selection, don't save yet
    setSelectedFrameId(frameId);
  };

  const handleViewPool = (collection) => {
    setSelectedCollection(collection);
    setGachaPoolDialogOpen(true);
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    setSaving(true);

    try {
      // Save avatar if one is selected
      if (selectedAvatarId && selectedAvatarId !== currentAvatar?.id) {
        await axios.post('/avatars/current', { avatarId: selectedAvatarId });
        const avatar = avatars.find(a => a.id === selectedAvatarId);
        setCurrentAvatar(avatar);
        
        // Update user info in localStorage
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          userData.avatar = avatar;
          localStorage.setItem('user', JSON.stringify(userData));
        }
      }

      // Save background preference
      if (selectedBackground) {
        setSidebarBackground(selectedBackground);
      }

      // Save frame if one is selected
      if (selectedFrameId && selectedFrameId !== currentFrame?.id) {
        await axios.post('/avatar-frames/current', { frameId: selectedFrameId });
        const frame = frames.find(f => f.id === selectedFrameId);
        setCurrentFrame(frame);
        
        // Update user info in localStorage
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          userData.currentFrame = frame;
          localStorage.setItem('user', JSON.stringify(userData));
          // Dispatch custom event to notify other components
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event('userFrameUpdated'));
          }
        }
      }
      
      // 这里可以添加更新用户信息的API调用
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setSuccess('设置已保存');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('保存失败：' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const sidebarConfig = createTeacherSidebarConfig({
    navigate,
    user,
    selectedCourse,
    onLogout: handleLogout,
    currentPath: location.pathname
  });
  const drawerContent = (
    <SidebarWithAccordion
      {...sidebarConfig}
      onSettingsClick={() => navigate('/teacher/settings')}
    />
  );

  return (
    <DashboardLayout
      drawerContent={drawerContent}
      appBarTitle="个人设置"
      courseName={selectedCourse?.name}
      courseTermCode={selectedCourse?.termCode}
      onLogout={handleLogout}
    >
      <Container maxWidth="md" sx={{ pt: 0, pb: 4, mt: -3 }}>
        <Paper elevation={0} sx={{ p: 4, bgcolor: 'transparent' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          <Stack spacing={3}>
            <Box>
              <Tabs value={mainTab} onChange={(e, newValue) => setMainTab(newValue)} sx={{ mb: 3 }}>
                <Tab label="个人设置" />
                <Tab label="头像管理" icon={<AvatarIcon />} iconPosition="start" />
                <Tab label="抽卡" icon={<GachaIcon />} iconPosition="start" />
                <Tab label="背景设置" icon={<WallpaperIcon />} iconPosition="start" />
              </Tabs>

              {/* 个人设置 Tab */}
              {mainTab === 0 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    基本信息
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Stack spacing={2}>
                    <TextField
                      label="姓名"
                      fullWidth
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      disabled={saving}
                    />
                  </Stack>
                </Box>
              )}

              {/* 头像管理 Tab */}
              {mainTab === 1 && (
                <>
                  {loadingAvatars || loadingFrames ? (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Stack spacing={4}>
                      {/* Avatar Frames Section */}
                      <Box>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                          头像框
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          收集徽章解锁更多精美头像框 ({frames.filter(f => f.unlocked).length} / {frames.length})
                        </Typography>
                        
                        <Grid container spacing={2}>
                          {frames.map((frame) => (
                            <Grid item xs={4} sm={3} md={2} key={frame.id}>
                              <AvatarFrameCard 
                                frame={frame} 
                                unlocked={frame.unlocked}
                                selected={selectedFrameId === frame.id}
                                onClick={() => handleSelectFrame(frame.id)}
                                hideName={true}
                                previewAvatar={currentAvatar}
                              />
                            </Grid>
                          ))}
                        </Grid>

                        {frames.length === 0 && (
                          <Box sx={{ py: 4, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              加载中...
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      <Divider />

                      {/* Owned Avatars Section */}
                      <Box>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                          头像
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          我的头像 ({avatars.filter(a => a.owned).length} / {avatars.length})
                        </Typography>
                        
                        {(() => {
                          const ownedAvatars = avatars.filter(a => a.owned);
                          const totalOwnedPages = Math.ceil(ownedAvatars.length / ownedAvatarsPerPage);
                          const startIndex = (ownedAvatarsPage - 1) * ownedAvatarsPerPage;
                          const endIndex = startIndex + ownedAvatarsPerPage;
                          const currentPageAvatars = ownedAvatars.slice(startIndex, endIndex);
                          
                          return (
                            <>
                              <Grid container spacing={2}>
                                {currentPageAvatars.map((avatar) => (
                                  <Grid item xs={4} sm={3} md={2} key={avatar.id}>
                                    <AvatarCard 
                                      avatar={avatar} 
                                      owned={true}
                                      selected={selectedAvatarId === avatar.id}
                                      onClick={() => handleSelectAvatar(avatar.id)}
                                      hideName={true}
                                      currentFrame={currentFrame}
                                    />
                                  </Grid>
                                ))}
                              </Grid>
                              
                              {totalOwnedPages > 1 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                  <Pagination
                                    count={totalOwnedPages}
                                    page={ownedAvatarsPage}
                                    onChange={(e, value) => setOwnedAvatarsPage(value)}
                                    color="primary"
                                    size="small"
                                  />
                                </Box>
                              )}
                            </>
                          );
                        })()}

                        {avatars.filter(a => a.owned).length === 0 && (
                          <Box sx={{ py: 4, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              暂无头像，点击上方按钮开始抽卡！
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Stack>
                  )}
                </>
              )}

              {/* 背景设置 Tab */}
              {mainTab === 3 && (
                <Box sx={{ borderRadius: 2, p: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    侧边栏页面背景
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    选择带侧边栏页面的背景效果（如课程总览、设置等）
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    {Object.values(BACKGROUND_OPTIONS).map((opt) => (
                      <Paper
                        key={opt.id}
                        onClick={() => {
                          setSelectedBackground(opt.id);
                          setSidebarBackground(opt.id);
                        }}
                        sx={{
                          p: 2,
                          minWidth: 140,
                          cursor: 'pointer',
                          border: 2,
                          borderColor: selectedBackground === opt.id ? 'primary.main' : 'divider',
                          bgcolor: selectedBackground === opt.id ? 'action.selected' : 'background.paper',
                          transition: 'all 0.2s',
                          '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' },
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={600}>{opt.label}</Typography>
                        <Typography variant="caption" color="text.secondary">{opt.description}</Typography>
                      </Paper>
                    ))}
                  </Stack>

                  <Typography variant="subtitle1" sx={{ mt: 3, mb: 2, fontWeight: 600 }}>
                    配色设计
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    选择网站主题配色（按钮、链接、卡片等）
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    {Object.values(COLOR_SCHEME_OPTIONS).map((opt) => {
                      const colors = opt.id === 'custom'
                        ? [customPalette.primary.main, customPalette.secondary.main, customPalette.background.default, customPalette.background.paper]
                        : (opt.colors || []).slice(0, 4);
                      return (
                        <Paper
                          key={opt.id}
                          onClick={() => {
                            setSelectedColorScheme(opt.id);
                            setColorScheme(opt.id);
                          }}
                          sx={{
                            p: 2,
                            minWidth: 140,
                            cursor: 'pointer',
                            border: 2,
                            borderColor: selectedColorScheme === opt.id ? 'primary.main' : 'divider',
                            bgcolor: selectedColorScheme === opt.id ? 'action.selected' : 'background.paper',
                            transition: 'all 0.2s',
                            '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' },
                          }}
                        >
                          <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
                            {colors.map((c, i) => (
                              <Box key={i} sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: c, border: '1px solid', borderColor: 'divider' }} />
                            ))}
                          </Stack>
                          <Typography variant="subtitle2" fontWeight={600}>{opt.label}</Typography>
                          <Typography variant="caption" color="text.secondary">{opt.description}</Typography>
                        </Paper>
                      );
                    })}
                  </Stack>

                  {selectedColorScheme === 'custom' && (
                    <Box sx={{ mt: 3, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>自定义颜色</Typography>
                      <Stack spacing={2}>
                        {[
                          { key: 'primary', label: '主色', path: 'primary.main' },
                          { key: 'secondary', label: '辅助色', path: 'secondary.main' },
                          { key: 'bgDefault', label: '背景色', path: 'background.default' },
                          { key: 'bgPaper', label: '卡片背景', path: 'background.paper' },
                          { key: 'textPrimary', label: '文字色', path: 'text.primary' },
                        ].map(({ key, label, path }) => {
                          const [a, b] = path.split('.');
                          const value = customPalette[a]?.[b] || '#ffffff';
                          return (
                            <Stack key={key} direction="row" alignItems="center" spacing={2}>
                              <Typography variant="body2" sx={{ minWidth: 80 }}>{label}</Typography>
                              <Box
                                component="input"
                                type="color"
                                value={value}
                                onChange={(e) => {
                                  const next = JSON.parse(JSON.stringify(customPalette));
                                  next[a][b] = e.target.value;
                                  setCustomPaletteState(next);
                                  setCustomPalette(next);
                                }}
                                sx={{ width: 40, height: 32, p: 0, border: '1px solid', borderColor: 'divider', borderRadius: 1, cursor: 'pointer', bg: 'transparent' }}
                              />
                              <Typography variant="caption" color="text.secondary">{value}</Typography>
                            </Stack>
                          );
                        })}
                      </Stack>
                    </Box>
                  )}
                </Box>
              )}

              {/* 抽卡 Tab */}
              {mainTab === 2 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 3 }}>
                    抽卡系统（教师免费无限抽卡）
                  </Typography>
                  
                  <Stack spacing={3}>
                    {/* 猫咪卡池 */}
                    <Paper
                      onClick={() => handleViewPool('cat')}
                      sx={{
                        position: 'relative',
                        width: '100%',
                        aspectRatio: '3/1',
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        overflow: 'hidden',
                        borderRadius: 2,
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 6
                        }
                      }}
                    >
                      {/* Background image */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          backgroundImage: 'url(/avatars/cover/cat.jpeg)',
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          backgroundRepeat: 'no-repeat'
                        }}
                      />
                      {/* Overlay gradient for better text readability */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(to right, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0) 100%)'
                        }}
                      />
                      {/* Content */}
                      <Box
                        sx={{
                          position: 'relative',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'flex-end',
                          alignItems: 'flex-end',
                          p: 4,
                          zIndex: 1
                        }}
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          size="large"
                          startIcon={<GachaIcon />}
                          sx={{
                            boxShadow: 3,
                            '&:hover': {
                              boxShadow: 6
                            }
                          }}
                        >
                          查看卡池
                        </Button>
                      </Box>
                    </Paper>

                    {/* 人类卡池 */}
                    <Paper
                      onClick={() => handleViewPool('human')}
                      sx={{
                        position: 'relative',
                        width: '100%',
                        aspectRatio: '3/1',
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        overflow: 'hidden',
                        borderRadius: 2,
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 6
                        }
                      }}
                    >
                      {/* Background image */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          backgroundImage: 'url(/avatars/cover/human.jpeg)',
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          backgroundRepeat: 'no-repeat'
                        }}
                      />
                      {/* Overlay gradient for better text readability */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(to right, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0) 100%)'
                        }}
                      />
                      {/* Content */}
                      <Box
                        sx={{
                          position: 'relative',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'flex-end',
                          alignItems: 'flex-end',
                          p: 4,
                          zIndex: 1
                        }}
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          size="large"
                          startIcon={<GachaIcon />}
                          sx={{
                            boxShadow: 3,
                            '&:hover': {
                              boxShadow: 6
                            }
                          }}
                        >
                          查看卡池
                        </Button>
                      </Box>
                    </Paper>
                  </Stack>
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? '保存中...' : '保存设置'}
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Container>
      
      <GachaPoolDialog
        open={gachaPoolDialogOpen}
        onClose={() => setGachaPoolDialogOpen(false)}
        collection={selectedCollection}
        avatars={avatars}
        userPoints={{ points: 0 }}
        gachaInfo={null}
        isTeacher={true}
        onGachaSuccess={fetchAvatars}
      />
    </DashboardLayout>
  );
}

export default TeacherSettingsPage;
